<template>
  <div class="h-100 d-flex flex-column">
    <!-- Row: title -->
    <div class="row f-row mb-2 markets-chart-title d-flex align-items-center">
      <div class="col-6" style="font-size: 1rem">
        Global Macroeconomic Indicators
      </div>
      <slot name="spot"></slot>
    </div>
    <!-- Row: selections -->
    <div class="row f-row justify-content-center">
      <div class="col col-4 mr-1">
        <div class="row f-row">
          <div class="col col-12">
            <Dropdown
              v-model="selectedMacroId"
              :options="
                availableMacroVars.map(function(d, i) {
                  return { asset: d, id: i };
                })
              "
              @change="onUserMacroSelection"
              optionLabel="asset"
              optionValue="id"
              placeholder="Select Variable"
              scrollHeight="400px"
              :filter="true"
            >
              <template #value="slotProps">
                {{ availableMacroVars[slotProps.value] }}
              </template>
              <template #option="slotProps">
                <div class="country-item">
                  <div>{{ slotProps.option.asset }}</div>
                </div>
              </template>
            </Dropdown>
          </div>
        </div>
      </div>
      <div class="col col-2 ml-1 mr-1">
        <div class="row f-row">
          <div class="col col-12">
            <Dropdown
              v-model="selectedCountry"
              :options="
                availableCountries.map(function(d, i) {
                  return { asset: d, id: i };
                })
              "
              @change="onUserCountrySelection"
              optionLabel="asset"
              optionValue="id"
              placeholder="Select Country"
              scrollHeight="400px"
              :filter="true"
            >
              <template #value="slotProps">
                {{ availableCountries[slotProps.value] }}
              </template>
              <template #option="slotProps">
                <div class="country-item">
                  <div>{{ slotProps.option.asset }}</div>
                </div>
              </template>
            </Dropdown>
          </div>
        </div>
      </div>
      <div class="col col-2 ml-1">
        <div class="row f-row">
          <div class="col col-12">
            <Dropdown
              v-model="selectedDateId"
              :options="
                dates.map(function(d, i) {
                  return { asset: d, id: i };
                })
              "
              @change="onUserDateSelection"
              optionLabel="asset"
              optionValue="id"
              placeholder="Select Country"
              scrollHeight="400px"
              :filter="true"
            >
              <template #value="slotProps">
                {{ dates[slotProps.value] | formatDate }}
              </template>
              <template #option="slotProps">
                <div class="country-item">
                  <div>{{ slotProps.option.asset | formatDate }}</div>
                </div>
              </template>
            </Dropdown>
          </div>
        </div>
      </div>
    </div>
    <!-- Row: Selections Labels -->
    <div class="row f-row justify-content-center" style="font-size: 0.8rem;">
      <div class="col col-4 text-left mr-1">
        <div class="row f-row">
          <div class="col col-12">
            Select Variable
          </div>
        </div>
      </div>
      <div class="col col-2 text-left ml-1 mr-1">
        <div class="row f-row">
          <div class="col col-12">
            Highlight Country
          </div>
        </div>
      </div>
      <div class="col col-2 text-left ml-1">
        <div class="row f-row">
          <div class="col col-12">
            Sort Country Values For Date
          </div>
        </div>
      </div>
    </div>
    <!-- Row: Chart -->
    <div class="row f-row mt-2 h-100">
      <div
        class="col col-12 text-center progress-div"
        v-if="showLoading"
        style="position: absolute; left: 0; top: 47.5%; z-index: 10000000;"
      >
        <QSLoading
          :isLoading="showLoading"
          :size="35"
          :colorTheme="colorTheme"
        />
      </div>
      <div
        class="col col-12 text-center"
        v-else-if="chartData.length === 0"
        style="position: absolute; left: 0; top: 45%; z-index: 10000000;"
      >
        Failed to fetch data.<br />Please refresh page or contact support.
      </div>
      <div class="col col-12" style="height:100%; width:100%">
        <div class="h-100 d-flex flex-column">
          <FundamentalsChart
            :chartData="chartData"
            :colorSelection="colorSelection"
            :colorTheme="colorTheme"
            :dateFormat="dateFormat"
            :formatYAxis="formatYAxis"
            :renderFundamentals="renderFundamentals"
            :selectedCountryId="selectedCountryId"
            :selectedDateId="selectedDateId"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { formatDate_YMD } from "@/_core/_helpers/_helpers";
import {
  colorSelection,
  dateFormat,
  formatPercent,
  numberFormat
} from "@/_core/_helpers/_parametric_tools";
import { keys, timeFormat, timeParse } from "d3";
import Dropdown from "primevue/dropdown";
import FundamentalsChart from "../markets/FundamentalsChart";
import { gatewayService } from "@/_services/gateway.service";
import QSLoading from "@/components/QSLoading.vue";

export default {
  name: "CurrencyFundamentals",
  components: {
    Dropdown,
    FundamentalsChart,
    QSLoading
  },
  props: {
    colorTheme: { required: true, type: Boolean, default: true },
    countrySpecs: { required: true },
    currencyStr: { required: true },
    // ============ Data Classes ===============
    processData: { required: true }
  },
  data() {
    return {
      availableCountries: ["UK", "MEX"],
      availableMacroVars: ["GDP"],
      chartData: {},
      chartDataRaw: {},
      colorSelection: colorSelection,
      countries_short: null,
      dataMacro: {},
      dateFormat: dateFormat,
      dates: [new Date()],
      formatPercent: formatPercent,
      formatYAxis: "",
      isoByCountry: { "UNITED KINGDOM": "UK" },
      numberFormat: numberFormat,
      refDate: null,
      renderFundamentals: false,
      selectedCountry: 0,
      selectedCountryId: "COL",
      selectedDateId: 0,
      selectedMacro: null,
      selectedMacroId: 0,
      showLoading: false
    };
  },
  created() {
    this.refDate = new Date(new Date().getFullYear(), 11, 31);
  },
  mounted() {
    let _this = this;
    this.formatYAxis = this.formatPercent;
    this.readMacroData();
    window.addEventListener("resize", function() {
      _this.callWidth();
    });
  },
  watch: {
    currencyStr() {
      this.getCountryName();
    }
  },
  filters: {
    formatDate: function(value) {
      if (value) {
        return timeFormat("%b %d, %y")(value);
      }
    }
  },
  methods: {
    getCountryName() {
      let curr =
        this.currencyStr.slice(0, 3) === "USD"
          ? this.currencyStr.slice(-3)
          : this.currencyStr.slice(0, 3);
      let country;
      this.countrySpecs.forEach(function(d) {
        if (d.currency_representation === curr) {
          country = d.iso;
        }
      });
      this.selectedCountryId = country;
      this.selectedCountry = this.availableCountries.indexOf(
        this.isoByCountry[country]
      );
      this.renderFundamentals += 1;
    },
    callWidth() {
      this.renderFundamentals += 1;
    },
    readMacroData() {
      let vis = this;
      this.showLoading = true;
      let today = new Date();
      let date = new Date(today.setFullYear(today.getFullYear() - 5));
      return gatewayService.market
        .macroIndicators({
          format: "json",
          initial_date: formatDate_YMD(date),
          today: formatDate_YMD(new Date())
        })
        .then(data => {
          data = JSON.parse(JSON.stringify(data));
          this.countries_short = keys(data[0]).slice(5);
          Object.assign(this, { isoByCountry: data[0] });
          var availableM = [];
          for (let i = 0; i < data.length; i++) {
            if (
              data[i].long_name !== "long_name" &&
              data[i].long_name !== "short_name"
            ) {
              data[i].observation_date = timeParse("%Y-%m-%d")(
                data[i].observation_date
              );
              availableM.push(data[i].long_name);
            }
          }
          availableM = availableM.sort();
          this.availableMacroVars = availableM.filter(
            this.processData.onlyUnique
          );
          // TODO: parche para datos que vienen incompletos
          let filterOut = [
            "EXPORTS OF GOODS AND SERVICES (% of GDP)",
            "IMPORTS OF GOODS AND SERVICES (% of GDP)"
          ];
          this.availableMacroVars = this.availableMacroVars.filter(
            d => filterOut.indexOf(d) === -1
          );
          var availableC = [];
          for (let i = 0; i < this.countries_short.length; i++) {
            availableC.push(data[0][this.countries_short[i]]);
          }
          Object.assign(this, { availableCountries: availableC });
          this.chartDataRaw = data;
          this.selectedMacro = this.availableMacroVars[this.selectedMacroId];
          const selM = this.selectedMacro;
          this.dataMacro = this.chartDataRaw.filter(d => d.long_name === selM);
          this.dates = this.dataMacro
            .map(d => d.observation_date)
            .sort((a, b) => +a - b);
          this.selectedDateId = this.dates.map(Number).indexOf(+this.refDate);
          if (this.selectedDateId < 0) {
            this.selectedDateId = this.dates.length - 5;
          }

          this.getCountryName();
          this.parseData();
        })
        .catch(function(error) {
          vis.showLoading = false;
          vis.chartData = [];
          console.log(error);
        });
    },
    onUserMacroSelection() {
      this.selectedMacro = this.availableMacroVars[this.selectedMacroId];
      const selM = this.selectedMacro;
      this.dataMacro = this.chartDataRaw.filter(d => d.long_name === selM);
      this.dates = this.dataMacro
        .map(d => d.observation_date)
        .sort((a, b) => +a - b);
      this.selectedDateId = Math.min(
        this.selectedDateId,
        this.dates.length - 1
      );
      this.parseData();
    },
    onUserCountrySelection() {
      let _this = this;
      this.selectedCountryId = Object.keys(this.isoByCountry).find(
        key =>
          _this.isoByCountry[key] ===
          _this.availableCountries[_this.selectedCountry]
      );
      this.parseData();
    },
    onUserDateSelection() {
      this.parseData();
    },
    parseData() {
      if (this.dataMacro[0].is_percentage === true) {
        this.formatYAxis = this.formatPercent;
      } else {
        this.formatYAxis = this.numberFormat;
      }
      var cData = {
        y: this.selectedMacro,
        series: this.dataMacro.map((d, i) => ({
          date: this.dates[i],
          values: Object.values(
            this.dataMacro.filter(
              k => +k.observation_date === +this.dates[i]
            )[0]
          )
            .slice(5)
            .map(l => (isFinite(l) ? +l : NaN))
        })),
        bind: this.dataMacro.map((d, i) => ({
          date: this.dates[i],
          y1: Object.values(
            this.dataMacro.filter(
              k => +k.observation_date === +this.dates[Math.max(0, i - 1)]
            )[0]
          )
            .slice(5)
            .map(l => (isFinite(l) ? +l : NaN)),
          y2: Object.values(
            this.dataMacro.filter(
              k => +k.observation_date === +this.dates[i]
            )[0]
          )
            .slice(5)
            .map(l => (isFinite(l) ? +l : NaN))
        })),
        country: this.countries_short,
        country_long: this.availableCountries,
        dates: this.dates
      };
      Object.assign(this, { chartData: cData });
      this.renderFundamentals += 1;
      this.showLoading = false;
    }
  }
};
</script>
